import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { ExtraFieldsTable } from '../../../components/ExtraFields';
import {
  ExtraFieldsSupportedObject,
  ExtraTableRenderSettings,
} from '../../../components/ExtraFields/types';
import { ExtraFieldsTableId } from '../../../components/ExtraFields/types';
import { Expense } from '../types';
import { useExpenseTableRenderSettings } from '../useExpenseTableRenderSettings';

interface ExpenseChildrenListProps {
  childExpenses: Expense[];
  isMyExpenses: boolean;
  isExpanded: boolean;
  showAddButton: boolean;
  onExpenseClick: (
    expense: Expense,
    showComments?: boolean,
    showItems?: boolean,
  ) => void;
  onDeleteExpense?: (expenseId: string) => void;
  onAddExpenseClick: (e: React.MouseEvent) => void;
}

export const ExpenseChildrenList: React.FC<ExpenseChildrenListProps> = ({
  childExpenses,
  isMyExpenses,
  isExpanded,
  showAddButton,
  onExpenseClick,
  onDeleteExpense,
  onAddExpenseClick,
}) => {
  const { t } = useTranslation();

  const { columns, afterPersistentColumns } = useExpenseTableRenderSettings({
    onItemClick: (item) => {
      const expense = childExpenses.find((exp) => exp.id === item.id);
      if (expense) {
        onExpenseClick(expense, item.showComments, item.showItems);
      }
    },
    onDeleteExpense,
    isMyExpenses,
  });

  const renderSettings: ExtraTableRenderSettings<Expense> = useMemo(
    () => ({
      columns,
      afterPersistentColumns,
    }),
    [columns, afterPersistentColumns],
  );

  if (!isExpanded) {
    return null;
  }

  return (
    <>
      <div className="expense-group-children">
        {childExpenses.length > 0 ? (
          <ExtraFieldsTable
            records={childExpenses}
            objectApiName={ExtraFieldsSupportedObject.Expense}
            tableId={
              isMyExpenses
                ? ExtraFieldsTableId.ExpensesMy
                : ExtraFieldsTableId.ExpensesManager
            }
            renderSettings={renderSettings}
          />
        ) : (
          <div className="expense-group-empty-state p-3 text-center">
            <p className="text-muted mb-0">{t('expenses.groups.noExpenses')}</p>
          </div>
        )}
      </div>
      {showAddButton && (
        <div className="expense-tile-add-expense-section pt-3">
          <button
            type="button"
            className="expense-add-expense-btn"
            onClick={onAddExpenseClick}>
            <FlairIcon icon="add-outline" size="sm" />
            <span>{t('expenses.groups.addExpense')}</span>
          </button>
        </div>
      )}
    </>
  );
};
